import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, Subject } from 'rxjs';

export type BucketMeta = {
  loading: boolean;
  loaded: boolean;
  failed: boolean;
  error?: { status: number } | null;
  statusCode?: number | null;
  meta?: any;
};

const initialBucketMeta: BucketMeta = {
  loading: false,
  loaded: false,
  failed: false,
  error: undefined,
};

const cleanupPayload = (payload: object) =>
  payload
    ? Object.fromEntries(
        Object.entries(payload).filter((_, value) => value !== undefined && value !== null),
      )
    : {};

export class Bucket<Data = any, Meta = BucketMeta, Event = any> {
  public static key(bucket: { name: string; payload: any }): string {
    const payloadString = JSON.stringify(bucket.payload, (key, value) => {
      if (typeof value === 'number') {
        return value.toString();
      }
      return value;
    });
    return `${bucket.name}?${payloadString}`;
  }

  public _name: string;
  public get name() {
    return this._name;
  }
  public set name(value: string) {
    this._name = value;
    this.key = Bucket.key(this);
  }
  public get payload() {
    return this._payload;
  }
  public set payload(value: any) {
    this._payload = cleanupPayload(value);
    this.key = Bucket.key(this);
  }
  public key: string;
  public events$ = new Subject<Event>();

  private _data$: BehaviorSubject<Data | null> = new BehaviorSubject(null);
  private _meta$: BehaviorSubject<Meta> = new BehaviorSubject(<any>initialBucketMeta);
  private _payload: any;

  constructor(name: string, payload: any, initialValue?: Data | null) {
    this._name = name;
    this._payload = cleanupPayload(payload);
    this.key = Bucket.key(this);
    if (initialValue) {
      this.setData(initialValue);
    }
  }

  public get data$(): Observable<Data | null> {
    // TODO we should use asObservable() but this would break some current code,
    // so let's cheat ts by mapping the type (BehaviorSubject inherits all methods of Observable anyway)
    return this._data$;
  }

  public get meta$(): Observable<Meta> {
    // TODO we should use asObservable() but this would break some current code,
    // so let's cheat ts by mapping the type (BehaviorSubject inherits all methods of Observable anyway)
    return this._meta$;
  }

  public get data(): Data | null {
    return this._data$.getValue();
  }

  public set data(nextData: Data | null) {
    this.setData(nextData);
  }

  public get meta(): Meta {
    return this._meta$.getValue();
  }

  public setData(nextData: Data | null): void {
    this._data$.next(nextData);
  }

  public setMeta(nextMeta: Meta): void {
    this._meta$.next(nextMeta);
  }

  public pushEvent(event: Event): void {
    this.events$.next(event);
  }
}

@Injectable({ providedIn: 'root' })
export class BucketsStore {
  private readonly buckets: Record<string, Record<string, Bucket>> = {};

  public getBucket<P = any>(
    name: string,
    payload: any = {},
    options: {
      cacheInMemory?: boolean;
      initialValue?: any;
    } = {},
  ): Bucket<P> {
    const cacheInMemory =
      typeof options.cacheInMemory === 'undefined' ? true : options.cacheInMemory;
    const initialValue = options.initialValue || null;
    const newPayload = cleanupPayload(payload);

    if (!cacheInMemory) {
      return new Bucket(name, newPayload, initialValue);
    }

    const bucketKey = Bucket.key({ name, payload: newPayload });
    const buckets = this.buckets[name] || (this.buckets[name] = {});
    return buckets[bucketKey] || (buckets[bucketKey] = new Bucket(name, payload, initialValue));
  }
}
